import { ActionIcon, Box, Button, Checkbox, Group, Menu, Select, Text, TextInput, UnstyledButton } from "@mantine/core"
import { ColumnsIcon, FunnelIcon, MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react"
import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import classes from "./catalog.module.css"
import type { FilterChip } from "./types"
import { getSortOptions, TABLE_PAGE_SIZES } from "./types"

type ToolbarProps = {
  search: string
  onSearchChange: (value: string) => void
  brandFilter: ReactNode
  promoOnly: boolean
  onPromoOnlyChange: (value: boolean) => void
  inStockOnly: boolean
  onInStockOnlyChange: (value: boolean) => void
  sort: string
  onSortChange: (value: string) => void
  /** Table view only: rows per page. */
  pageSize?: number
  onPageSizeChange?: (value: number) => void
  /** Table view only: optional columns the buyer can hide («Баганууд»). */
  columns?: { key: string; label: string; visible: boolean }[]
  onToggleColumn?: (key: string) => void
  /** Narrow screens: the rail moves into a drawer. */
  onOpenFilters?: () => void
}

/** Search + brand + «Урамшуулалтай» / «Нөөцтэй» + the one sort control (B15). */
export function SearchBar({
  search,
  onSearchChange,
  brandFilter,
  promoOnly,
  onPromoOnlyChange,
  inStockOnly,
  onInStockOnlyChange,
  sort,
  onSortChange,
  pageSize,
  onPageSizeChange,
  columns,
  onToggleColumn,
  onOpenFilters,
}: ToolbarProps) {
  const { t } = useTranslation(["order", "common"])

  return (
    <Box className={classes.toolbar}>
      {onOpenFilters && (
        <ActionIcon variant="default" size={42} radius="md" aria-label={t("common:filter.title")} onClick={onOpenFilters}>
          <FunnelIcon size={18} />
        </ActionIcon>
      )}
      <TextInput
        value={search}
        onChange={(e) => onSearchChange(e.currentTarget.value)}
        placeholder={t("order:catalog.searchPlaceholder")}
        aria-label={t("order:catalog.searchPlaceholder")}
        leftSection={<MagnifyingGlassIcon size={16} />}
        size="md"
        radius="md"
        style={{ flex: "1 1 220px", minWidth: 0 }}
        rightSection={
          search ? (
            <ActionIcon variant="subtle" color="gray" aria-label={t("common:actions.clear")} onClick={() => onSearchChange("")}>
              <XIcon size={14} />
            </ActionIcon>
          ) : null
        }
      />
      <Button
        variant="default"
        radius="md"
        h={42}
        className={classes.toggleChip}
        data-active={promoOnly || undefined}
        aria-pressed={promoOnly}
        onClick={() => onPromoOnlyChange(!promoOnly)}
      >
        {t("order:catalog.chipPromo")}
      </Button>
      <Button
        variant="default"
        radius="md"
        h={42}
        className={classes.toggleChip}
        data-active={inStockOnly || undefined}
        aria-pressed={inStockOnly}
        onClick={() => onInStockOnlyChange(!inStockOnly)}
      >
        {t("order:catalog.chipInStock")}
      </Button>
      {brandFilter}
      <Select
        data={getSortOptions((key) => t(key))}
        value={sort}
        onChange={(value) => onSortChange(value || "name-asc")}
        allowDeselect={false}
        aria-label={t("order:catalog.sortLabel")}
        size="md"
        radius="md"
        w={250}
        leftSection={
          <Text span fz="var(--nw-fz-body)" c="var(--nw-text-2)">
            {t("order:catalog.sortLabel")}
          </Text>
        }
        leftSectionWidth={72}
        styles={{ input: { fontWeight: 600 } }}
      />
      {pageSize && onPageSizeChange && (
        <Select
          data={TABLE_PAGE_SIZES.map((n) => ({ value: String(n), label: String(n) }))}
          value={String(pageSize)}
          onChange={(value) => onPageSizeChange(Number(value) || TABLE_PAGE_SIZES[1])}
          allowDeselect={false}
          aria-label={t("order:catalog.pageSize")}
          size="md"
          radius="md"
          w={170}
          leftSection={
            <Text span fz="var(--nw-fz-body)" c="var(--nw-text-2)">
              {t("order:catalog.pageSize")}
            </Text>
          }
          leftSectionWidth={96}
          styles={{ input: { fontWeight: 600 } }}
        />
      )}
      {columns && onToggleColumn && (
        <Menu position="bottom-end" withinPortal closeOnItemClick={false}>
          <Menu.Target>
            <Button variant="default" radius="md" h={42} leftSection={<ColumnsIcon size={16} />}>
              {t("order:catalog.columns")}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {columns.map((col) => (
              <Menu.Item key={col.key} onClick={() => onToggleColumn(col.key)}>
                <Checkbox checked={col.visible} readOnly label={col.label} tabIndex={-1} style={{ pointerEvents: "none" }} />
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      )}
    </Box>
  )
}

type ActiveFiltersProps = {
  chips: FilterChip[]
  onRemove: (chip: FilterChip) => void
  onClearAll: () => void
}

/** «Шүүлт: Повер банк × REMAX × … Бүгдийг цэвэрлэх» (canvas OrdB_CatalogFilters). */
export function ActiveFilters({ chips, onRemove, onClearAll }: ActiveFiltersProps) {
  const { t } = useTranslation("order")
  if (chips.length === 0) return null
  return (
    <Group gap="xs" wrap="wrap">
      <Text fz="var(--nw-fz-body)" fw={600}>
        {t("catalog.filtersLabel")}
      </Text>
      {chips.map((chip) => (
        <Box key={`${chip.type}-${chip.value}`} component="span" className={classes.pill}>
          {chip.label}
          <ActionIcon
            size="sm"
            variant="transparent"
            color="dark"
            aria-label={t("catalog.removeFilter", { label: chip.label })}
            onClick={() => onRemove(chip)}
          >
            <XIcon size={12} />
          </ActionIcon>
        </Box>
      ))}
      <UnstyledButton className={classes.linkButton} onClick={onClearAll}>
        {t("catalog.clearAll")}
      </UnstyledButton>
    </Group>
  )
}
